import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';

const Dashboard = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  // API Base URL - prefer device host for mobile access
  // const deriveApiBase = () => {
  //   try {
  //     const { protocol, hostname } = window.location;
  //     if (hostname === 'localhost' || hostname === '127.0.0.1') {
  //       return process.env.REACT_APP_API_URL || 'http://localhost/api';
  //     }
  //     return `${protocol}//${hostname}/api`;
  //   } catch (_) {
  //     return process.env.REACT_APP_API_URL || 'http://localhost/api';
  //   }
  // };
  const deriveApiBase = () => {
    // Force production URL when deployed
    if (window.location.hostname === 'rccgtr.net' || window.location.hostname.includes('rccgtr')) {
      return 'https://rccgtr.net/api';
    }
    
    // Development only
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    }
    
    // Fallback to relative path
    return '/api';
  };
  const API_BASE_URL = deriveApiBase();

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError('');

      // Load visitor statistics
      const { data: visitorsData } = await axios.get(`${API_BASE_URL}/visitors/stats`);

      // Load SMS statistics
      const { data: smsData } = await axios.get(`${API_BASE_URL}/sms/stats`);

      if (visitorsData.success && smsData.success) {
        setStats({
          visitors: visitorsData.data,
          sms: smsData.data,
        });
      } else {
        setError('Failed to load dashboard statistics');
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError('Error loading dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format birth date
  const formatBirthDate = (birthDate) => {
    if (!birthDate) return '';
    const date = new Date(birthDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };

  // Helper function to get gender data with fallbacks
  const getGenderData = () => {
    const genderData = stats?.visitors?.demographics?.gender || [];
    const maleData = genderData.find(g => g.gender?.toLowerCase() === 'male') || { count: 0, percentage: 0 };
    const femaleData = genderData.find(g => g.gender?.toLowerCase() === 'female') || { count: 0, percentage: 0 };
    
    return { maleData, femaleData };
  };

  // Helper function to get age data with fallbacks
  const getAgeData = () => {
    const ageData = stats?.visitors?.demographics?.age || [];
    const groups = ['13-19','20-24','25-29','30-34','35-39','40-44','45-49','50-54','55-59','60+'];
    const ageGroups = {};
    groups.forEach(g => {
      ageGroups[g] = ageData.find(a => a.age_group === g) || { count: 0, percentage: 0 };
    });
    return ageGroups;
  };

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Dashboard">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button onClick={loadDashboardStats} className="ml-4 text-red-800 underline">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const { maleData, femaleData } = getGenderData();
  const ageGroups = getAgeData();
  const birthdaysThisMonth = stats?.visitors?.birthdays?.thisMonth || [];
  const todaysBirthdays = stats?.visitors?.birthdays?.today || [];

  return (
    <Layout title="Dashboard">
      <div className="max-w-6xl mx-auto">

        {/* Centralized RCCG Title */}
      <div className="text-center text-2xl font-bold mb-6 text-indigo-700 dark:text-indigo-300">
        RCCG The Throneroom
      </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Guest Mannagment Portal Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Overview of church activities and SMS statistics across our two Sunday services
          </p>
        </div>

        {/* Service Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* First Service Card */}
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            } border-l-4 border-indigo-500`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
                🏛️ Sunday First Service
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">10:30 AM</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">First Timers:</span>
                <span className="font-semibold">{stats?.sms?.firstService?.firstTimers || 0}</span>
                {/* <span className="font-semibold">{stats?.visitors?.total || 0}</span> */}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Returning Members:</span>
                <span className="font-semibold">
                  {stats?.sms?.firstService?.returningMembers || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total SMS sent:</span>
                <span className="font-semibold text-indigo-600">
                  {stats?.sms?.firstService?.totalSMS || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Second Service Card */}
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            } border-l-4 border-green-500`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-green-600 dark:text-green-400">
                ⛪ Sunday Second Service
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">12:30 PM</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">First Timers:</span>
                <span className="font-semibold">{stats?.sms?.secondService?.firstTimers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Returning Members:</span>
                <span className="font-semibold">
                  {stats?.sms?.secondService?.returningMembers || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total SMS Sent:</span>
                <span className="font-semibold text-green-600">
                  {stats?.sms?.secondService?.totalSMS || 0}
                </span>
              </div>
            </div>
          </div>

          {/* General Statistics Card */}
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            } border-l-4 border-purple-500`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                🔔 General Services
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">Other</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Midweek SMS:</span>
                <span className="font-semibold">{stats?.sms?.midweek?.totalSMS || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Birthday SMS:</span>
                <span className="font-semibold">{stats?.sms?.birthday?.totalSMS || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Guests:</span>
                <span className="font-semibold text-purple-600">{stats?.visitors?.total || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Visitor Breakdown */}
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <h3 className="text-xl font-semibold mb-4">Guest Breakdown by Service for the Month</h3>
            <div className="space-y-4">
              {stats?.visitors?.serviceBreakdown?.map((service, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="font-medium">
                    {service.service_attended === 'first_service'
                      ? '🏛️ First Service'
                      : service.service_attended === 'second_service'
                      ? '⛪ Second Service'
                      : service.service_attended === 'expression_church'
                      ? '🔔 Expression Church'
                      : '🔔 Midweek'}
                  </span>
                  <span className="text-lg font-semibold">{service.count}</span>
                </div>
              ))}
            </div>
          </div>

{/* Birthdays This Month */}
<div
            className={`rounded-md w-full max-w-xl md:w-4/6 p-4 shadow text-sm ${
              isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex justify-between">
              <p className="mt-6 font-bold">Birthdays This Month</p>
              <p className="text-right text-xs mt-6 cursor-pointer hover:underline">
                View All ({birthdaysThisMonth.length})
              </p>
            </div>
            <hr className="mt-5" />
            
            {/* Show today's birthdays first if any */}
            {todaysBirthdays.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-green-600 mb-2">🎉 Today's Birthdays</p>
                {todaysBirthdays.slice(0, 2).map((person, index) => (
                  <div key={`today-${index}`} className="flex justify-between items-center border-b py-2 bg-green-50 dark:bg-green-900/20 rounded px-2">
                    <div>
                      <p className="font-semibold">{person.name}</p>
                      <p className="text-xs text-green-600 dark:text-green-400">Today!</p>
                    </div>
                    <p className="text-xs text-green-600 font-medium">{person.phone || 'No phone'}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Show upcoming birthdays this month */}
            {birthdaysThisMonth.length > 0 ? (
              birthdaysThisMonth.slice(0, 5).map((person, index) => (
                <div key={index} className="flex justify-between items-center border-b py-2">
                  <div>
                    <p className="font-semibold">{person.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-300">
                      {formatBirthDate(person.birth_date)}
                    </p>
                  </div>
                  <p className="text-xs text-green-600 font-medium">{person.phone || 'No phone'}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No birthdays this month</p>
              </div>
            )}
          </div>
          
        </div>

        {/* Member Demographics + Birthdays This Month */}
        <div className="flex flex-col gap-6 mt-8 mb-8 w-full items-center md:flex-row md:justify-between font-bold">
          {/* Member Demographics */}
          <div
            className={`shadow-2xl w-full max-w-xl md:w-8/12 p-5 rounded-xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <h1 className={`font-bold mt-6 ${isDark ? 'text-indigo-100' : 'text-indigo-900'}`}>
              Guest Demographics
            </h1>
            <hr className={`${isDark ? 'border-indigo-700' : 'border-indigo-700'} mt-2`} />
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 w-full">
              <div className="flex-1">
                <h1
                  className={`text-xs font-bold mt-5 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  Gender Distribution
                </h1>
                <div className="flex mt-5 gap-4 items-center">
                  <h1 className="text-xs font-bold">Male</h1>
                  <div 
                    className="p-1 rounded-2xl bg-blue-600"
                    style={{ width: `${Math.max(maleData.percentage * 2, 10)}px` }}
                  ></div>
                  <p className="text-xs font-bold ml-4">{maleData.percentage}% ({maleData.count})</p>
                </div>
                <div className="flex mt-5 gap-4 items-center">
                  <h1 className="text-xs font-bold">Female</h1>
                  <div 
                    className="p-1 rounded-2xl bg-pink-600"
                    style={{ width: `${Math.max(femaleData.percentage * 2, 10)}px` }}
                  ></div>
                  <p className="text-xs font-bold ml-4">{femaleData.percentage}% ({femaleData.count})</p>
                </div>
              </div>
              <div className="flex-1">
                <h1
                  className={`text-xs font-bold mt-5 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  Age Distribution
                </h1>
                {Object.entries(ageGroups).map(([ageGroup, data]) => (
                  <div key={ageGroup} className="flex mt-5 gap-4 items-center">
                    <h1 className="text-xs font-bold">{ageGroup}</h1>
                    <div 
                      className="p-1 rounded-2xl bg-green-600"
                      style={{ width: `${Math.max(data.percentage * 1.5, 8)}px` }}
                    ></div>
                    <p className="text-xs font-bold ml-4">{data.percentage}% ({data.count})</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Recent Activity */}
        <div
          className={`rounded-xl shadow-lg p-6 ${
            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
          }`}
        >
          <h3 className="text-xl font-semibold mb-4">Recent Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3 text-indigo-600 dark:text-indigo-400">
                📊 This Month's Guests
              </h4>
              <div className="text-3xl font-bold text-indigo-600">
                {stats?.visitors?.thisMonth || 0}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">New guests this month</p>
            </div>
            {/* <div>
              <h4 className="font-medium mb-3 text-green-600 dark:text-green-400">
                👥 Total People Invited
              </h4>
              <div className="text-3xl font-bold text-green-600">
                {stats?.visitors?.totalPeople || 0}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Including family and friends
              </p>
            </div> */}
          </div>
        </div>

        {/* Service Information */}
        <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
          <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 text-lg">
            ℹ️ Service Schedule & Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-blue-300">
            <div>
              <p>
                <strong>🏛️ First Sunday Service SMS:</strong> 10:30 AM
              </p>
              <p className="ml-4">• First Time Visitors</p>
              <p className="ml-4">• Returning Members</p>
            </div>
            <div>
              <p>
                <strong>⛪ Second Sunday Service SMS:</strong> 12:30 PM
              </p>
              <p className="ml-4">• First Time Visitors</p>
              <p className="ml-4">• Returning Members</p>
            </div>
            <div>
              <p>
                <strong>🔔 Midweek Service:</strong> Wednesday 06:00 PM
              </p>
              <p className="ml-4">• Bible Study</p>
              <p className="ml-4">• Prayer Meetings</p>
            </div>
            <div>
              <p>
                <strong>🎂 Birthday SMS:</strong> Daily at 8:00 AM
              </p>
              <p className="ml-4">• Automatic greetings</p>
              <p className="ml-4">• Stops after 5 messages</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
